import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { logDev } from "@/lib/log";
import { useAuth } from "@/context/AuthContext";

const AdminPendingContext = createContext(null);

const EMPTY = { video_requests: 0, ebook_requests: 0, total: 0 };

/**
 * Polls /api/admin/pending-counts when logged-in as admin so nav badges + tab
 * dots refresh without needing SSE plumbing. Cheap: two count_documents calls.
 */
export function AdminPendingProvider({ children }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [counts, setCounts] = useState(EMPTY);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    if (!isAdmin) { setCounts(EMPTY); return; }
    try {
      const { data } = await api.get("/admin/pending-counts");
      setCounts(data);
    } catch (err) {
      // Transient network hiccups shouldn't spam the toast; log for debugging.
      logDev("Admin pending-counts poll failed:", err);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) { setCounts(EMPTY); return; }
    refresh();
    timer.current = setInterval(refresh, 30000);
    return () => clearInterval(timer.current);
  }, [isAdmin, refresh]);

  const value = useMemo(() => ({ counts, refresh }), [counts, refresh]);
  return <AdminPendingContext.Provider value={value}>{children}</AdminPendingContext.Provider>;
}

export function useAdminPending() {
  return useContext(AdminPendingContext) || { counts: EMPTY, refresh: () => {} };
}
